import { useState } from 'react';
import PromptBlock from './PromptBlock';
import TopicSettingsModal from './TopicSettingsModal';
import DocumentsPanel from './DocumentsPanel';

// ─── Icons ────────────────────────────────────────────────────────────────────

function PencilIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Sidebar({
  width,
  collapsed,
  onToggleCollapse,
  onResizeStart,  
  sessions,
  activeId,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onDeleteChats,
  onUpdateSession,
  tools,
  defaultSystemPrompt,
  prompts,
  onDeletePrompt,
  onSelectPrompt,
  documents,
  onUploadDocument,
  onIngestUrl,
  onDeleteDocument,
  onReassessDocument,
  currentUser,
  onLogout,
}) {
  const [settingsSession, setSettingsSession] = useState(null);
  const [promptsExpanded, setPromptsExpanded] = useState(true);
  const [selectMode, setSelectMode]           = useState(false);
  const [selected, setSelected]               = useState(new Set());

  const openSettings = (session, e) => {
    e.stopPropagation();
    setSettingsSession(session);
  };

  const handleSaveSettings = ({ name, saveToKbByDefault, systemPrompt, toolConfig }) => {
    onUpdateSession(settingsSession.id, { name, saveToKbByDefault, systemPrompt, toolConfig });
    setSettingsSession(null);
  };

  const enterSelectMode = () => { setSelectMode(true); setSelected(new Set()); };
  const exitSelectMode  = () => { setSelectMode(false); setSelected(new Set()); };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selected.size === sessions.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sessions.map(s => s.id)));
    }
  };

  const handleBulkDelete = () => {
    onDeleteChats([...selected]);
    exitSelectMode();
  };

  return (
    <>
      {/* Collapse toggle — always visible, sits outside the aside */}
      <button
        className={`sidebar-collapse-btn ${collapsed ? 'sidebar-collapse-btn--collapsed' : ''}`}
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{ left: collapsed ? 0 : 'calc(var(--sidebar-width) - 1px)' }}
      >
        {collapsed ? '›' : '‹'}
      </button>
      <aside className="sidebar" style={{ width: collapsed ? 0 : 'var(--sidebar-width)' }}>
        {/* Drag handle */}
        <div className="resize-handle" onMouseDown={onResizeStart} title="Drag to resize" />

          {/* Brand area */}
          <div className="sidebar-brand">
          <div className="brand-name">obiwan ❯</div>
          <span className="brand-model">Personal AI assistant</span>
        </div>

        {/* Topic list — scrollable */}
        <div className="sidebar-topics">
          <button className="new-chat-btn" onClick={onNewChat}>
            + New Topic
          </button>

          {/* Session list header */}
          <div className="session-list-header">
            <div className="session-list-header-left">
              <span className="session-list-title">Topics</span>
              <span className="session-list-count">{sessions.length}</span>
            </div>
            {!selectMode ? (
              <button
                className="session-list-trash-btn"
                onClick={enterSelectMode}
                title="Select topics to delete"
              >
                <TrashIcon />
              </button>
            ) : (
              <button className="session-list-cancel-btn" onClick={exitSelectMode}>
                cancel
              </button>
            )}
          </div>

          {selectMode && selected.size > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-action-count">{selected.size} selected</span>
              <button className="bulk-delete-btn" onClick={handleBulkDelete}>
                <TrashIcon /> delete {selected.size}
              </button>
            </div>
          )}

          {selectMode && sessions.length > 0 && (
            <button className="select-all-btn" onClick={handleSelectAll}>
              {selected.size === sessions.length ? 'deselect all' : 'select all'}
            </button>
          )}

          <div className="session-list">
            {sessions.map(session => (
              <div
                key={session.id}
                onClick={(e) => selectMode ? toggleSelect(session.id, e) : onSelectChat(session.id)}
                className={`session-item ${!selectMode && activeId === session.id ? 'session-active' : ''} ${selectMode && selected.has(session.id) ? 'session-selected' : ''}`}
              >
                {selectMode ? (
                  <input
                    type="checkbox"
                    className="session-checkbox"
                    checked={selected.has(session.id)}
                    onChange={e => toggleSelect(session.id, e)}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  /* session-dot hidden via CSS; active uses border-left */
                  <div className="session-dot" />
                )}

                <span className="session-name">{session.name || session.id}</span>

                {!selectMode && session.saveToKbByDefault && (
                  <span className="session-kb-badge" title="Save to KB by default">KB</span>
                )}
                {!selectMode && session.system_prompt && (
                  <span className="session-kb-badge session-sp-badge" title="Custom system prompt">SP</span>
                )}

                {!selectMode && (
                  <div className="session-actions">
                    <button
                      title="Topic settings"
                      onClick={e => openSettings(session, e)}
                      className="action-btn"
                    >
                      <PencilIcon />
                    </button>
                    <button
                      title="Delete"
                      onClick={e => { e.stopPropagation(); onDeleteChat(session.id); }}
                      className="action-btn action-btn-danger"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Prompts panel */}
        {activeId && !selectMode && (
          <div className="sidebar-prompts-panel">
            <button
              className="sidebar-prompts-header"
              onClick={() => setPromptsExpanded(v => !v)}
              aria-expanded={promptsExpanded}
            >
              <span>⊕</span>
              <span>Prompts</span>
              {prompts.length > 0 && (
                <span className="sidebar-prompts-count">{prompts.length}</span>
              )}
              <svg
                className={`sidebar-prompts-chevron ${promptsExpanded ? 'chevron-up' : ''}`}
                width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {promptsExpanded && (
              <div className="sidebar-prompts-body">
                {prompts.length === 0 ? (
                  <div className="prompts-empty">
                    <span>no prompts saved for this topic.</span>
                    <span>use ⊕ on any message to save.</span>
                  </div>
                ) : (
                  <div className="prompt-list">
                    {prompts.map(prompt => (
                      <PromptBlock
                        key={prompt.id}
                        prompt={prompt}
                        onDelete={onDeletePrompt}
                        onSelect={onSelectPrompt}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Documents panel */}
        {activeId && !selectMode && (
          <DocumentsPanel
            sessionId={activeId}
            documents={documents}
            onUploadMany={onUploadDocument}
            onIngestUrl={onIngestUrl}
            onDelete={onDeleteDocument}
            onReassess={onReassessDocument}
          />
        )}

        {/* User footer */}
        {currentUser && (
          <div className="sidebar-user-footer">
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{currentUser.username}</span>
            </div>
            <button
              className="sidebar-logout-btn"
              onClick={onLogout}
              title="Sign out"
            >
              ⏻
            </button>
          </div>
        )}

        {settingsSession && (
          <TopicSettingsModal
            session={settingsSession}
            tools={tools}
            defaultSystemPrompt={defaultSystemPrompt}
            onSave={handleSaveSettings}
            onClose={() => setSettingsSession(null)}
          />
        )}
      </aside>
    </>
  );
}